import { cloneTemplate, fillFields } from "../dom";

// builds the month grid, weeks start on monday

export interface DayMarks {
  tasks: number;
  events: number;
  // colours of linked calendars with something on the day
  linked?: string[];
  // short lines shown inside the cell when the month is wide enough to read them
  labels?: DayLabel[];
}

export interface DayLabel {
  text: string;
  // start time, dropped first when the cell is narrow
  time?: string;
  kind: "task" | "event" | "shared" | "linked";
  color?: string;
}

// how many lines a roomy cell lists before it says "+n"
const LABELS_PER_DAY = 3;

function buildLabels(labels: DayLabel[]): HTMLElement {
  const list = document.createElement("span");
  list.className = "day-labels";
  for (const label of labels.slice(0, LABELS_PER_DAY)) {
    const line = document.createElement("span");
    line.className = `day-label kind-${label.kind}`;
    if (label.time) {
      const time = document.createElement("span");
      time.className = "day-label-time";
      time.textContent = `${label.time} `;
      line.append(time);
    }
    line.append(label.text);
    if (label.color) line.style.setProperty("--link-color", label.color);
    list.append(line);
  }
  if (labels.length > LABELS_PER_DAY) {
    const more = document.createElement("span");
    more.className = "day-label day-label-more";
    more.textContent = `+${labels.length - LABELS_PER_DAY}`;
    list.append(more);
  }
  return list;
}

export function isoDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function buildDot(kind: string): HTMLElement {
  const dot = cloneTemplate("template-dot");
  dot.classList.add(kind);
  return dot;
}

function buildDay(date: Date, inMonth: boolean, marks: DayMarks | undefined, onPick: (day: string) => void): HTMLElement {
  const day = isoDate(date);
  const cell = fillFields(cloneTemplate("template-day"), { number: String(date.getDate()) });
  cell.dataset.date = day;
  cell.classList.toggle("outside", !inMonth);
  cell.classList.toggle("today", day === isoDate(new Date()));

  // one dot per kind of thing happening that day
  const dots = cell.querySelector(".day-dots")!;
  if (marks?.tasks) dots.append(buildDot("dot-task"));
  if (marks?.events) dots.append(buildDot("dot-event"));
  for (const color of marks?.linked ?? []) {
    const dot = buildDot("dot-linked");
    dot.style.setProperty("--link-color", color);
    dots.append(dot);
  }

  if (marks?.labels?.length) {
    cell.append(buildLabels(marks.labels));
    cell.setAttribute("aria-label", `${date.getDate()}: ${marks.labels.map((label) => `${label.time ? `${label.time} ` : ""}${label.text}`).join(", ")}`);
  }

  cell.addEventListener("click", () => onPick(day));
  return cell;
}

export function buildMonth(month: Date, marksByDay: Map<string, DayMarks>, onPick: (day: string) => void): HTMLElement[] {
  const first = new Date(month.getFullYear(), month.getMonth(), 1);
  const offset = (first.getDay() + 6) % 7;
  const start = new Date(first.getFullYear(), first.getMonth(), 1 - offset);

  // always six weeks so the grid never jumps in height
  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
    return buildDay(date, date.getMonth() === month.getMonth(), marksByDay.get(isoDate(date)), onPick);
  });
}
